import MDEditor from '@uiw/react-md-editor';
import { useFormik } from 'formik';
import { useEffect, useRef, memo, useCallback } from 'react';
import { useShallow } from 'zustand/react/shallow';
// mantine
import { Spoiler } from '@mantine/core';
// components
import type { TAttribute, TAttributeData } from '@newo/api/gateway';
import AttributeValue from '@/pages/Settings/components/AttributeValue';
// utils
import validationSchema from '@/pages/Settings/components/Attribute/Attribute.schema.ts';
import useSettingsStore from '@/pages/Settings/Settings.store';

interface AttributeProps {
    attribute: TAttribute;
}

const Attribute = ({ attribute }: AttributeProps) => {
    const { id, ...rest } = attribute;

    const { setAttributesValues, setAttributesErrors } = useSettingsStore(
        useShallow((state) => ({
            setAttributesValues: state.setAttributesValues,
            setAttributesErrors: state.setAttributesErrors
        }))
    );
    const changedValue = useSettingsStore(
        (state) => state.attributesValues[attribute.id]
    );

    const initialValues: TAttributeData = {
        ...rest,

        value: changedValue?.value ?? attribute.value
    };

    const formik = useFormik({
        initialValues,
        validationSchema,
        onSubmit: () => {
            /** keep empty */
        },
        validateOnChange: true,
        validateOnBlur: true,
        enableReinitialize: true
    });

    const prevValueRef = useRef(formik.values.value);
    const prevErrorRef = useRef(formik.errors.value);

    const handleValueChange = useCallback(() => {
        if (
            formik.isValid &&
            formik.dirty &&
            prevValueRef.current !== formik.values.value
        ) {
            prevValueRef.current = formik.values.value;
            setAttributesValues({ [id]: formik.values });
        }
    }, [formik.isValid, formik.dirty, formik.values, id, setAttributesValues]);

    const handleErrorChange = useCallback(() => {
        if (prevErrorRef.current !== formik.errors.value) {
            prevErrorRef.current = formik.errors.value;
            setAttributesErrors({ [id]: formik.errors.value });
        }
    }, [formik.errors.value, id, setAttributesErrors]);

    useEffect(() => {
        handleValueChange();
    }, [handleValueChange]);

    useEffect(() => {
        handleErrorChange();
    }, [handleErrorChange]);

    return (
        <article className="flex flex-col gap-2 relative rounded-lg bg-[#FAFAFA] p-4 border-1 border-solid border-[#DEE2E6]/50">
            <div className="flex gap-4">
                <div className="flex-1 break-all">
                    {attribute.title ? (
                        <>
                            <span className="font-semibold">
                                {attribute.title}
                            </span>{' '}
                            <span className="text-gray-4">/</span>{' '}
                        </>
                    ) : undefined}
                    <span className="text-gray-6 text-xs">{attribute.idn}</span>
                </div>
            </div>

            <Spoiler hideLabel="Hide" maxHeight={400} showLabel="Show more">
                <div data-color-mode="light">
                    <MDEditor.Markdown
                        className="[&_pre>code]:whitespace-break-spaces! [&_p]:mb-1!"
                        source={attribute.description ?? ''}
                        style={{
                            backgroundColor: 'transparent',
                            fontSize: '0.85rem'
                        }}
                    />
                </div>
            </Spoiler>

            <div className="flex flex-col gap-3">
                <AttributeValue formik={formik} />
            </div>
        </article>
    );
};

export default memo(Attribute);
